"use client";
import { motion } from "framer-motion";
import { usePortfolio } from "@/hooks/usePortfolio";
import { AdminLoadingSpinner } from "@/components/admin/AdminComponents";
import { ProjectCardSkeleton } from "@/components/ui/ProjectCardSkeleton";
import Image from "next/image";

export default function ProjectsSection() {
  const { data, loading, error } = usePortfolio();

  if (loading) {
    return (
      <section id="projects" className="py-20 bg-white dark:bg-[#020817]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4 relative inline-block">
              My Projects
              <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-blue-400 to-blue-300 dark:from-blue-700 dark:via-blue-500 dark:to-blue-400"></div>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              A showcase of my recent work and technical achievements
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <ProjectCardSkeleton />
            <ProjectCardSkeleton />
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-500 mb-4">Error Loading Projects</h2>
          <p className="text-gray-600 dark:text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  const { projects } = data;

  return (
    <section id="projects" className="py-10 sm:py-16 md:py-20 bg-white dark:bg-[#020817]">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 relative inline-block">
            My Projects
            <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-blue-400 to-blue-300 dark:from-blue-700 dark:via-blue-500 dark:to-blue-400"></div>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            A showcase of my recent work and technical achievements
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12 w-full">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, scale: 0.85, filter: 'blur(8px)', boxShadow: '0 0 0px 0px #00eaff' }}
              whileInView={{
                opacity: 1,
                scale: 1,
                filter: 'blur(0px)',
                boxShadow: [
                  '0 0 0px 0px #00eaff',
                  '0 0 32px 8px #00eaff',
                  '0 0 0px 0px #00eaff'
                ]
              }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{
                type: "spring",
                stiffness: 60,
                damping: 18,
                duration: 0.8,
                boxShadow: { duration: 0.7, times: [0, 0.5, 1] },
                delay: index * 0.08
              }}
              className="group relative bg-gradient-to-br from-white via-gray-50 to-blue-50 dark:from-[#0f1729] dark:via-[#162037] dark:to-[#1c2744] rounded-2xl overflow-hidden border border-gray-200 dark:border-blue-900/30 backdrop-blur-xl hover:border-blue-400/50 transition-all duration-500 w-full"
            >
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-blue-400/10 to-blue-300/10 dark:from-blue-500/10 dark:via-blue-400/10 dark:to-blue-300/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative h-48 sm:h-64 md:h-[300px] overflow-hidden">
                <Image
                  src={project.image || "/placeholder-project.jpg"}
                  alt={project.title}
                  fill
                  className="object-cover transform group-hover:scale-110 transition-transform duration-700 opacity-80 group-hover:opacity-100"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  loading="lazy"
                  placeholder="blur"
                  blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white/80 via-white/30 to-transparent dark:from-[#1c2744] dark:via-[#1c2744]/50 dark:to-transparent" />
                {/* Tech Stack - Shown on Hover */}
                <div className="absolute bottom-0 left-0 right-0 p-2 sm:p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500 bg-gradient-to-t from-white/90 to-transparent dark:from-[#1c2744] dark:to-transparent">
                  <div className="flex flex-wrap gap-1 sm:gap-2 justify-center">
                    {project.technologies.map((tech, i) => (
                      <span
                        key={i}
                        className="px-2 py-0.5 sm:px-3 sm:py-1 text-xs sm:text-sm font-medium bg-gradient-to-r from-blue-100/40 to-blue-50/40 text-blue-700 dark:from-blue-500/10 dark:to-blue-400/10 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30 rounded-full"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
                {/* Project Links */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                    <div className="flex gap-2 sm:gap-4 flex-wrap justify-center">
                      {project.projectUrl && (
                        <a
                          href={project.projectUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-blue-500/20 to-blue-400/20 text-blue-900 dark:text-white border border-blue-400/50 rounded-xl hover:bg-blue-400/30 transition-all duration-300 backdrop-blur-sm font-medium flex items-center gap-2 hover:scale-105 text-xs sm:text-base"
                        >
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          View Live
                        </a>
                      )}
                      {project.githubUrl && (
                        <a
                          href={project.githubUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-gray-500/20 to-gray-400/20 text-gray-900 dark:text-white border border-gray-400/50 rounded-xl hover:bg-gray-400/30 transition-all duration-300 backdrop-blur-sm font-medium flex items-center gap-2 hover:scale-105 text-xs sm:text-base"
                        >
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.237 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                          </svg>
                          View Code
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* Content Section */}
              <div className="p-4 sm:p-6 md:p-8 relative">
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white group-hover:text-blue-400 transition-colors duration-300 mb-2 sm:mb-4">
                  {project.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-700 dark:text-gray-400 mb-4 sm:mb-6 line-clamp-3">
                  {project.shortDescription || project.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {projects.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">
              No projects available at the moment.
            </p>
          </div>
        )}
      </div>
    </section>
  );
} 